import { useEffect, useRef } from 'react'

import { Box } from '@chakra-ui/react'

/**
 * Distance from the pointer to the swatch, so the swatch never covers what is being sampled.
 */
const TARGET_COLOR_CURSOR_OFFSET = 16 // px

/** Above the canvas and the toolbars, which sit at 10. */
const TARGET_COLOR_CURSOR_Z_INDEX = 20

const TARGET_COLOR_CURSOR_EVENT = 'editor:tool:polyline:targetColorCursor'

/**
 * Set the colour the eyedropper swatch shows, or hide it by passing `undefined`.
 *
 * Only the colour travels this way. The swatch tracks the pointer on its own, off the raw
 * `mousemove`, because the tool only learns the colour from the canvas raycast — which is
 * throttled to 30fps and far too coarse for something that has to sit under the cursor.
 *
 * Deliberately a DOM event rather than React state: this fires on every sampled move, and
 * re-rendering the canvas config per move is the editor's known performance trap.
 *
 * @param colorHex Colour under the pointer, or undefined when there is nothing to preview.
 */
export const setTargetColorCursor = (colorHex: string | undefined) => {
  document.dispatchEvent(new CustomEvent<string | undefined>(TARGET_COLOR_CURSOR_EVENT, { detail: colorHex }))
}

/**
 * Eyedropper swatch that follows the cursor while a チョーキング detection waits for its colour
 * pick, showing the colour of the mesh under the pointer.
 *
 * Mounted for the whole editor session and driven entirely through the DOM, so it costs nothing
 * beyond a pointer position write until the Polyline tool starts sending it colours.
 */
const TargetColorCursor = () => {
  // Refs
  const swatchRef = useRef<HTMLDivElement>(null)
  const colorHexRef = useRef<string | undefined>(undefined)
  const pointerRef = useRef<{ x: number; y: number } | null>(null)
  const frameRef = useRef<number | null>(null)

  /**
   * Track the pointer and the pushed colour in refs, and apply both to the DOM node once per
   * animation frame. Nothing here goes through React or through the canvas raycast.
   */
  useEffect(() => {
    const paint = () => {
      frameRef.current = null

      const swatch = swatchRef.current
      if (!swatch) return

      const colorHex = colorHexRef.current
      const pointer = pointerRef.current

      // No colour means there is nothing under the pointer worth previewing.
      if (!colorHex || !pointer) {
        swatch.style.opacity = '0'
        return
      }

      swatch.style.opacity = '1'
      swatch.style.backgroundColor = colorHex
      swatch.style.transform = `translate3d(${pointer.x + TARGET_COLOR_CURSOR_OFFSET}px, ${
        pointer.y + TARGET_COLOR_CURSOR_OFFSET
      }px, 0)`
    }

    const schedule = () => {
      if (frameRef.current === null) frameRef.current = requestAnimationFrame(paint)
    }

    const handleColor = (event: Event) => {
      colorHexRef.current = (event as CustomEvent<string | undefined>).detail
      schedule()
    }

    const handlePointerMove = (event: MouseEvent) => {
      pointerRef.current = { x: event.clientX, y: event.clientY }

      // Idle unless the tool is actively feeding us a colour.
      if (colorHexRef.current) schedule()
    }

    document.addEventListener(TARGET_COLOR_CURSOR_EVENT, handleColor)
    document.addEventListener('mousemove', handlePointerMove, { passive: true })

    return () => {
      document.removeEventListener(TARGET_COLOR_CURSOR_EVENT, handleColor)
      document.removeEventListener('mousemove', handlePointerMove)
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    }
  }, [])

  return (
    <Box
      ref={swatchRef}
      data-testid="polyline-target-color-cursor"
      position="fixed"
      top={0}
      left={0}
      boxSize="24px"
      borderRadius="full"
      borderWidth="2px"
      borderColor="white"
      boxShadow="0 0 0 1px rgba(0, 0, 0, 0.6)"
      opacity={0}
      pointerEvents="none"
      zIndex={TARGET_COLOR_CURSOR_Z_INDEX}
    />
  )
}

export default TargetColorCursor
